import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import {
    CreateTotpMfaTokenRequest,
    ResponseCode,
    VerifyTotpMfaTokenRequest
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    AuthenticationService,
    forLifeOf,
    LifecycleAware,
    MFAType,
    focusOnFirstInvalidLsqInput,
    LsqValidators
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { LoginPageStep, LoginService } from '../login.service';
import {
    CreateTotpMfaTokenService,
    VerifyTotpMfaTokenService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import { ApiRoutes } from '@lsq-frontend/angular-shared/app-constants';

@LifecycleAware()
@Component({
    selector: 'totp-mfa-registration',
    styleUrls: ['./login-step.scss'],
    template: `
        <div *ngIf="pageLoading">
            <lsq-circle-loader></lsq-circle-loader>
        </div>
        <div class="enrollTOTP" *ngIf="!pageLoading">
            <form class="login-step-wide" [formGroup]="totpFormGroup" (ngSubmit)="submitTOTPCode()">
                <h1>Multi-Factor Authentication</h1>
                <p class="body" [style.marginBottom]="'2rem'">
                    To keep your account secure, we require the use of multi-factor authentication.
                    This provides an extra layer of protection in the event your email and password
                    are compromised.
                </p>
                <p class="body" [style.marginBottom]="'2rem'">
                    To get started, download the
                    <a
                        href="https://duo.com/product/multi-factor-authentication-mfa/duo-mobile-app"
                        target="_blank"
                        >Duo Mobile app</a
                    >
                    to your device, then select the Add option under Accounts. Scan the QR code
                    below, and then input the verification code provided to you by Duo Mobile.
                </p>
                <div class="qrcodeImage">
                    <qrcode
                        [qrdata]="this.qrCode"
                        [cssClass]="'center'"
                        [colorDark]="'#000000ff'"
                        [colorLight]="'#ffffffff'"
                        [elementType]="'canvas'"
                        [errorCorrectionLevel]="'M'"
                        [imageHeight]="75"
                        [imageWidth]="75"
                        [margin]="4"
                        [scale]="1"
                        [width]="200"
                        [allowEmptyString]="true"
                    ></qrcode>
                </div>
                <p class="label" [style.marginBottom]="'-0.5rem'">Enter Verification Code</p>
                <lsq-input
                    [style.marginBottom]="'1rem'"
                    name="totpCode"
                    [basicLabel]="false"
                    formControlName="totpCode"
                    [maxLength]="6"
                    [autoFocus]="true"
                    [group]="totpFormGroup"
                >
                </lsq-input>

                <div class="button-container">
                    <lsq-button
                        appearance="secondary"
                        size="block"
                        [buttonClass]="'back-btn'"
                        (buttonClick)="goBack()"
                        class="login-button-small"
                        [style.marginRight]="'2rem'"
                    >
                        Back
                    </lsq-button>
                    <lsq-button
                        type="submit"
                        size="block"
                        [submitting]="isSubmitting"
                        class="login-button-small"
                    >
                        Continue
                    </lsq-button>
                </div>
            </form>
        </div>
    `
})
export class TotpMfaRegistrationComponent implements OnInit {
    public isSubmitting = false;
    private _mfaCodeFailure = false;
    public qrCode: string;
    public mfaType: MFAType;
    public pageLoading = false;

    public totpFormGroup = this._formBuilder.group({
        totpCode: [
            '',
            [
                Validators.required,
                LsqValidators.number,
                LsqValidators.exactLength(6),
                // This lets us show a custom error message on the input.
                () =>
                    this._mfaCodeFailure
                        ? { totpCodeInvalid: { message: this._invalidAuthCodeCopy } }
                        : null
            ]
        ]
    });

    private readonly _invalidAuthCodeCopy = this._i18n({
        value: 'Invalid Code'
    });

    constructor(
        private _i18n: I18n,
        private _errorToastBlacklistService: ErrorToastBlacklistService,
        private _formBuilder: FormBuilder,
        private _loginService: LoginService,
        private _createTotpMfaTokenService: CreateTotpMfaTokenService,
        private _verifyTotpMfaTokenService: VerifyTotpMfaTokenService,
        private _authProviderService: AuthenticationService
    ) {
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${ApiRoutes.Api}/${ApiRoutes.VerifyTotpMfaToken}`,
            ResponseCode.InvalidRequest
        );

        this.totpFormGroup.valueChanges.pipe(forLifeOf(this)).subscribe(() => {
            this._mfaCodeFailure = false;
        });
    }

    public async submitTOTPCode(): Promise<void> {
        this._mfaCodeFailure = false;
        this.totpFormGroup.markAllAsTouched();
        this.totpFormGroup.get('totpCode').updateValueAndValidity();

        if (this.totpFormGroup.valid) {
            this.isSubmitting = true;

            const totpCode = this.totpFormGroup.get('totpCode').value;

            try {
                const response = await this._verifyTotpMfaTokenService.executeOnce(
                    new VerifyTotpMfaTokenRequest({ userCode: totpCode })
                );
                if (response.code !== ResponseCode.Success) {
                    throw new Error();
                } else {
                    this._loginService.userIsRegisteredForMfa = true;
                    await this._authProviderService.setUserPreferredMfa('SOFTWARE_TOKEN_MFA');
                    await this._loginService.finishLoginForTOTP();
                }
            } catch (error) {
                this._mfaCodeFailure = true;
                this.totpFormGroup.get('totpCode').updateValueAndValidity();
                focusOnFirstInvalidLsqInput();
            }

            this.isSubmitting = false;
        }
    }

    public async ngOnInit(): Promise<void> {
        this.pageLoading = true;
        const createTotpResponse = await this._createTotpMfaTokenService.executeOnce(
            new CreateTotpMfaTokenRequest()
        );
        const secretCode = createTotpResponse.totpToken;
        const username = `FastTrack`;
        const issuer = `LSQ`;
        this.qrCode = `otpauth://totp/AWSCognito:${username}?secret=${secretCode}&issuer=${issuer}`;
        this.pageLoading = false;
    }

    public goBack(): void {
        this._loginService.setLoginPageStep(LoginPageStep.MfaRegistration);
    }
}
